import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  Circle,
  useLoadScript,
} from "@react-google-maps/api";
import Select from "react-select";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { API_URL } from "..";
import Switch from "react-switch";

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const AddAppointmentLayer = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBtQlR_uF7q-S8TQblCzLw7Fy7BvyIeXAg",
    libraries: ["places"],
  });

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "8px",
    border: "1px solid #ced4da",
  };

  const defaultCenter = { lat: 51.5074, lng: -0.1276 };

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [customerType, setCustomerType] = useState("new");
  const [isJobLocationCustomerAddress, setIsJobLocationCustomerAddress] = useState(true);
  const [jobLocation, setJobLocation] = useState("");
  const [jobPostcode, setJobPostcode] = useState("");
  const [referenceNo, setreferenceNo] = useState("");
  const [jobLocationCoordinates, setJobLocationCoordinates] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    job_cost: "0",
    description: "",
    latitude: "",
    longitude: "",
  });
  const [showCustomerInfo, setShowCustomerInfo] = useState(true);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "",
    issuedDate: getCurrentDate(),
    dueDate: getFourteenthDate(),
    orderNumber: "",
  });
  const [companyData, setCompanyData] = useState({
    id: null,
    name: "",
    address: "",
    email: "",
    phone: "",
    vatRegNo: "",
    logo: "",
    accountNumber: "",
    accountTitle: "",
    sortCode: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [electricianListSelect, setElectricianListSelect] = useState([]);
  const [workforceListSelect, setWorkforceListSelect] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [newItem, setNewItem] = useState({
    itemType: "stock",
    itemName: null,
    qty: "",
    unitPrice: "",
    description: "",
  });
  const [optionsForServices, setOptionsForServices] = useState([]);
  const [electricianList, setElectricianList] = useState([]);
  const [workforceList, setWorkforceList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [jobLocationSuggestions, setJobLocationSuggestions] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [errors, setErrors] = useState({});
  const [discount, setDiscount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [enableGeofencing, setEnableGeofencing] = useState(false);
  const [radius, setRadius] = useState(100);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [enableAttachments, setEnableAttachments] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");

  // Refs for Quill editors and inputs
  const addressInputRef = useRef(null);
  const jobLocationInputRef = useRef(null);
  const quillRefs = {
    jobDescription: useRef(null),
    invoiceItem: useRef(null),
  };
  const quillInstances = {
    jobDescription: useRef(null),
    invoiceItem: useRef(null),
  };
  const fileInputRef = useRef(null);

  // Utility functions
  function getCurrentDate() {
    return new Date().toISOString().split("T")[0];
  }

  function getFourteenthDate() {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().split("T")[0];
  }

  const fetchCoordinates = async (address) => {
    if (!address) return { latitude: "", longitude: "" };
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB`
      );
      const data = await response.json();
      if (data.features?.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        return { latitude, longitude };
      }
      return { latitude: "", longitude: "" };
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return { latitude: "", longitude: "" };
    }
  };

  // API calls
  const fetchData = useCallback(async (endpoint, setter, formatter) => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "GET",
        headers: { Accept: "*/*", "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (response.ok) {
        setter(formatter ? formatter(data) : data);
      } else {
        throw new Error(data.message || `Failed to fetch ${endpoint}`);
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: error.message,
      });
    }
  }, []);

  useEffect(() => {
    const endpoints = [
      {
        path: "/api/invoice/invoice-number",
        setter: (data) => setInvoiceData((prev) => ({ ...prev, invoiceNumber: data.counter })),
      },
      {
        path: "/api/invoice/stock/fetch",
        setter: setStockList,
        formatter: (data) => data.stock.map((e) => ({ label: e.title, value: e.id })),
      },
      {
        path: "/api/service",
        setter: setOptionsForServices,
        formatter: (data) => data.map((e) => ({ label: e.title, value: e.id })),
      },
      {
        path: "/api/company",
        setter: setCompanyList,
        formatter: (data) => data.companies.map((company) => ({
          label: company.name,
          value: company.id,
          ...company,
        })),
      },
      {
        path: "/api/user/fetch-electrician",
        setter: setElectricianList,
        formatter: (data) => data.data.map((e) => ({ label: e.name, value: e.id })),
      },
      {
        path: "/api/workforce/select-workforce",
        setter: setWorkforceList,
        formatter: (data) => data.data.map((e) => ({ label: e.fullName, value: e.id })),
      },
      {
        path: "/api/customer/fetch",
        setter: setCustomerList,
        formatter: (data) => data.customer.map((e) => ({
          label: e.name,
          value: e.id,
          ...e,
        })),
      },
    ];

    endpoints.forEach(({ path, setter, formatter }) => fetchData(path, setter, formatter));
  }, [fetchData]);

  // Address suggestions
  useEffect(() => {
    const fetchSuggestions = async (query, type) => {
      if (!query) {
        type === "customer" ? setAddressSuggestions([]) : setJobLocationSuggestions([]);
        return;
      }
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            query
          )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB`
        );
        const data = await response.json();
        const suggestions = data.features.map((feature) => ({
          label: feature.place_name,
          value: feature.place_name,
          coordinates: feature.geometry.coordinates,
        }));
        type === "customer" ? setAddressSuggestions(suggestions) : setJobLocationSuggestions(suggestions);
      } catch (error) {
        console.error(`Error fetching ${type} address suggestions:`, error);
      }
    };

    const handleInput = (value, type) => {
      if (type === "customer") {
        setFormData((prev) => ({ ...prev, address: value, latitude: "", longitude: "" }));
      } else {
        setJobLocation(value);
        setJobLocationCoordinates(null);
      }
      fetchSuggestions(value, type);
    };

    const customerInput = addressInputRef.current;
    const jobInput = jobLocationInputRef.current;

    const customerHandler = (e) => handleInput(e.target.value, "customer");
    const jobHandler = (e) => handleInput(e.target.value, "job");

    if (customerInput) {
      customerInput.addEventListener("input", customerHandler);
    }
    if (jobInput && !isJobLocationCustomerAddress) {
      jobInput.addEventListener("input", jobHandler);
    }

    return () => {
      if (customerInput) {
        customerInput.removeEventListener("input", customerHandler);
      }
      if (jobInput) {
        jobInput.removeEventListener("input", jobHandler);
      }
    };
  }, [isJobLocationCustomerAddress]);

  // Initialize Quill editors
  const initializeQuill = useCallback((refKey, placeholder, onTextChange, initialContent = "") => {
    const ref = quillRefs[refKey].current;
    if (ref && !quillInstances[refKey].current) {
      const quill = new Quill(ref, {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: "" }, { align: "center" }, { align: "right" }],
          ],
        },
        placeholder,
      });

      quill.root.setAttribute("dir", "ltr");
      quill.root.style.direction = "ltr";
      quill.root.style.textAlign = "left";

      const editor = ref.querySelector(".ql-editor");
      if (editor) {
        editor.setAttribute("dir", "ltr");
        editor.style.direction = "ltr";
        editor.style.textAlign = "left";
      }

      if (initialContent) {
        quill.root.innerHTML = initialContent;
      }

      quill.on("text-change", () => {
        onTextChange(quill.root.innerHTML);
      });

      quillInstances[refKey].current = quill;
    }
  }, []);

  // Quill editor initialization
  useEffect(() => {
    if (currentStep === 2) {
      initializeQuill(
        "jobDescription",
        "Enter job description",
        (content) => {
          setFormData((prev) => ({ ...prev, description: content }));
        },
        formData.description
      );
    }

    if (generateInvoice && currentStep === 4) {
      initializeQuill(
        "invoiceItem",
        "Enter item description",
        (content) => {
          setNewItem((prev) => ({ ...prev, description: content }));
        },
        newItem.description
      );
    }

    return () => {
      if (quillInstances.jobDescription.current && currentStep !== 2) {
        quillInstances.jobDescription.current.off("text-change");
        quillInstances.jobDescription.current = null;
      }
      if (quillInstances.invoiceItem.current && (!generateInvoice || currentStep !== 4)) {
        quillInstances.invoiceItem.current.off("text-change");
        quillInstances.invoiceItem.current = null;
      }
    };
  }, [currentStep, generateInvoice, initializeQuill, formData.description, newItem.description]);

  // Map updates
  useEffect(() => {
    if (isJobLocationCustomerAddress && formData.latitude && formData.longitude) {
      setMarkerPosition({
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude),
      });
      setMapCenter({
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude),
      });
    } else if (!isJobLocationCustomerAddress && jobLocationCoordinates) {
      setMarkerPosition({
        lat: jobLocationCoordinates.latitude,
        lng: jobLocationCoordinates.longitude,
      });
      setMapCenter({
        lat: jobLocationCoordinates.latitude,
        lng: jobLocationCoordinates.longitude,
      });
    }
  }, [formData.latitude, formData.longitude, jobLocationCoordinates, isJobLocationCustomerAddress]);

  // Attachment handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter((file) =>
      ["image/jpeg", "image/png", "video/mp4", "video/mpeg"].includes(file.type)
    );

    if (validFiles.length + attachments.length > 5) {
      setAttachmentError("Maximum 5 files allowed.");
      return;
    }

    const newAttachments = validFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type.startsWith("image") ? "image" : "video",
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
    setAttachmentError("");
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const newAttachments = prev.filter((_, i) => i !== index);
      prev[index].url && URL.revokeObjectURL(prev[index].url);
      return newAttachments;
    });
  };

  // Handlers
  const handleCustomerTypeChange = (e) => {
    setCustomerType(e.target.value);
    setSelectedCustomer(null);
    setFormData({
      client_name: "",
      email: "",
      phone: "",
      address: "",
      postcode: "",
      job_cost: "0",
      description: formData.description,
      latitude: "",
      longitude: "",
    });
    setAddressSuggestions([]);
    setErrors({});
    setAlert({ show: false, type: "", message: "" });
  };

  const handleCustomerChange = async (selected) => {
    setSelectedCustomer(selected);
    setErrors((prev) => ({ ...prev, customer: null }));
    if (selected) {
      const { latitude, longitude } = await fetchCoordinates(selected.address);
      setFormData((prev) => ({
        ...prev,
        client_name: selected.name,
        email: selected.email,
        phone: selected.phone,
        address: selected.address,
        postcode: selected.postcode,
        latitude,
        longitude,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        client_name: "",
        email: "",
        phone: "",
        address: "",
        postcode: "",
        latitude: "",
        longitude: "",
      }));
    }
  };

  const handleAddressSelect = (selected) => {
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        address: selected.value,
        latitude: selected.coordinates[1],
        longitude: selected.coordinates[0],
      }));
      setAddressSuggestions([]);
      setErrors((prev) => ({ ...prev, address: null }));
    }
  };

  const handleJobLocationSelect = (selected) => {
    if (selected) {
      setJobLocation(selected.value);
      setJobLocationCoordinates({
        latitude: selected.coordinates[1],
        longitude: selected.coordinates[0],
      });
      setJobLocationSuggestions([]);
      setErrors((prev) => ({ ...prev, jobLocation: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleInvoiceChange = (field, value) => {
    setInvoiceData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected);
    setCompanyData(selected || {
      id: null,
      name: "",
      address: "",
      email: "",
      phone: "",
      vatRegNo: "",
      logo: "",
      accountNumber: "",
      accountTitle: "",
      sortCode: "",
    });
  };

  const handleItemChange = (field, value) => {
    setNewItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleChangeWorkforce = (selected) => {
    setWorkforceListSelect(selected || []);
    setErrors((prev) => ({ ...prev, workforce: null }));
  };

  const handleChangeElectrician = (selected) => {
    setElectricianListSelect(selected || []);
    setErrors((prev) => ({ ...prev, electricians: null }));
  };

  const handleChangeService = (selectedOptions) => {
    setSelectedServices(selectedOptions ? selectedOptions.map((option) => option.value) : []);
    setErrors((prev) => ({ ...prev, services: null }));
  };

  const onChangeStock = async (value) => {
    try {
      const response = await fetch(
        `${API_URL}/api/invoice/stock/fetch-single/${value.value}`,
        {
          method: "GET",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch stock details");
      const data = await response.json();
      setNewItem({
        ...newItem,
        itemName: value,
        unitPrice: data.stock.sellPrice || "",
        description: data.stock.description || "",
      });
      if (quillInstances.invoiceItem.current) {
        quillInstances.invoiceItem.current.root.innerHTML = data.stock.description || "";
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to fetch stock details",
      });
    }
  };

  const onChangeService = async (value) => {
    try {
      const response = await fetch(`${API_URL}/api/service/${value.value}`, {
        method: "GET",
        headers: { Accept: "*/*", "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch service details");
      const data = await response.json();
      setNewItem({
        ...newItem,
        itemName: value,
        qty: "1",
        description: data.description || "",
        unitPrice: data.price || "",
      });
      if (quillInstances.invoiceItem.current) {
        quillInstances.invoiceItem.current.root.innerHTML = data.description || "";
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to fetch service details",
      });
    }
  };

  const addItem = () => {
    const newErrors = {};
    if (!newItem.itemName) newErrors.itemName = "Item is required";
    if (!newItem.unitPrice) newErrors.unitPrice = "Unit price is required";
    if (newItem.itemType === "stock" && !newItem.qty)
      newErrors.qty = "Quantity is required";
    if (!newItem.description.trim() || newItem.description === "<p><br></p>")
      newErrors.description = "Description is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const qty = newItem.itemType === "service" ? 1 : parseFloat(newItem.qty);
    setInvoiceItems([
      ...invoiceItems,
      {
        id: invoiceItems.length + 1,
        itemName: newItem.itemName,
        qty,
        unitPrice: parseFloat(newItem.unitPrice),
        price: (qty * parseFloat(newItem.unitPrice)).toFixed(2),
        description: newItem.description.trim(),
      },
    ]);
    setNewItem({
      itemType: "stock",
      itemName: null,
      qty: "",
      unitPrice: "",
      description: "",
    });
    if (quillInstances.invoiceItem.current) {
      quillInstances.invoiceItem.current.root.innerHTML = "";
    }
    setErrors({});
  };

  const removeItem = (id) => {
    setInvoiceItems(invoiceItems.filter((item) => item.id !== id));
  };

  const calculateSubtotal = () =>
    invoiceItems.reduce((acc, item) => acc + parseFloat(item.price), 0);

  const calculateDiscount = () => (calculateSubtotal() * discount) / 100;

  const calculateTax = () =>
    ((calculateSubtotal() - calculateDiscount()) * taxAmount) / 100;

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    const tax = calculateTax();
    return subtotal - discountAmount + tax;
  };

  const createCustomer = async () => {
    const obj = {
      name: formData.client_name,
      address: formData.address,
      phone: formData.phone,
      email: formData.email,
      postcode: formData.postcode,
      map_location: formData.latitude && formData.longitude
        ? `${formData.latitude},${formData.longitude}`
        : "",
    };
    try {
      const response = await fetch(
        `${API_URL}/api/customer/signup-without-password`,
        {
          method: "POST",
          body: JSON.stringify(obj),
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (response.status === 201) {
        return data.customer;
      }
      throw new Error(data.message || "Failed to create customer");
    } catch (error) {
      throw new Error(`Failed to create customer: ${error.message}`);
    }
  };

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!referenceNo) newErrors.referenceNo = "Reference No is required";
      newErrors.client_name =
        !formData.client_name && customerType === "new"
          ? "Client name is required"
          : null;
      newErrors.email = !formData.email
        ? "Email is required"
        : !/\S+@\S+\.\S+/.test(formData.email)
        ? "Invalid email format"
        : null;
      newErrors.address =
        !formData.address && customerType === "new"
          ? "Address is required"
          : null;
      newErrors.customer =
        customerType === "existing" && !selectedCustomer
          ? "Please select a customer"
          : null;
      if (!isJobLocationCustomerAddress && !jobLocation)
        newErrors.jobLocation = "Job location is required";
      if (!isJobLocationCustomerAddress && !jobPostcode)
        newErrors.jobPostcode = "Job postcode is required";
    } else if (currentStep === 2) {
      newErrors.electricians =
        electricianListSelect.length === 0
          ? "At least one electrician is required"
          : null;
      newErrors.workforce =
        workforceListSelect.length === 0
          ? "At least one workforce member is required"
          : null;
      newErrors.services =
        selectedServices.length === 0
          ? "At least one service is required"
          : null;
    } else if (generateInvoice && currentStep === 3) {
      newErrors.invoiceNumber = !invoiceData.invoiceNumber
        ? "Order form number is required"
        : null;
      newErrors.issuedDate = !invoiceData.issuedDate
        ? "Issued date is required"
        : null;
      newErrors.dueDate = !invoiceData.dueDate ? "Due date is required" : null;
    } else if (generateInvoice && currentStep === 4) {
      newErrors.items =
        invoiceItems.length === 0 ? "At least one item is required" : null;
    } else if (generateInvoice && currentStep === 5) {
      newErrors.company = !selectedCompany ? "Please select a company" : null;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== null);
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleStepClick = (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
      setErrors({});
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      email: "",
      phone: "",
      address: "",
      postcode: "",
      job_cost: "0",
      description: "",
      latitude: "",
      longitude: "",
    });
    setInvoiceData({
      invoiceNumber: "",
      issuedDate: getCurrentDate(),
      dueDate: getFourteenthDate(),
      orderNumber: "",
    });
    setSelectedServices([]);
    setElectricianListSelect([]);
    setWorkforceListSelect([]);
    setSelectedCustomer(null);
    setSelectedCompany(companyList[0] || null);
    setCustomerType("new");
    setInvoiceItems([]);
    setDiscount(0);
    setTaxAmount(0);
    setGenerateInvoice(false);
    setAddressSuggestions([]);
    setJobLocationSuggestions([]);
    setErrors({});
    setAlert({ show: false, type: "", message: "" });
    setCurrentStep(1);
    setIsJobLocationCustomerAddress(true);
    setJobLocation("");
    setJobLocationCoordinates(null);
    setMarkerPosition(null);
    setMapCenter(defaultCenter);
    setJobPostcode("");
    setEnableAttachments(false);
    setAttachments([]);
    setAttachmentError("");
    setreferenceNo("")
    setEnableGeofencing(false)
    setRadius(100)
  };

  const onFormSubmission = async (event) => {
    event.preventDefault();
    if (!validateStep()) {
      setAlert({
        show: true,
        type: "danger",
        message: "Please correct the errors in the form.",
      });
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        window.location.href = "/sign-in";
        return;
      }

      let customerData = selectedCustomer;
      let latitude = formData.latitude;
      let longitude = formData.longitude;

      if (customerType === "new") {
        if (!latitude || !longitude) {
          const coords = await fetchCoordinates(formData.address);
          latitude = coords.latitude;
          longitude = coords.longitude;
        }
        customerData = await createCustomer();
      } else {
        customerData = {
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone,
          address: selectedCustomer.address,
          postcode: selectedCustomer.postcode,
          id: selectedCustomer.value,
        };
      }

      const mapLocation = isJobLocationCustomerAddress
        ? `${latitude},${longitude}`
        : jobLocationCoordinates
        ? `${jobLocationCoordinates.latitude},${jobLocationCoordinates.longitude}`
        : "";

      const formDataToSend = new FormData();
      formDataToSend.append("client_name", customerData.name);
      formDataToSend.append("email", customerData.email);
      formDataToSend.append("phone", customerData.phone);
      formDataToSend.append("referenceNo", referenceNo);
      formDataToSend.append("address", isJobLocationCustomerAddress ? customerData.address : jobLocation);
      formDataToSend.append("postcode", isJobLocationCustomerAddress ? customerData.postcode : jobPostcode);
      formDataToSend.append("job_cost", generateInvoice ? calculateTotal() : formData.job_cost);
      formDataToSend.append("job_posted_by", "company");
      formDataToSend.append(
        "service_required",
        selectedServices
          .map((id) => optionsForServices.find((s) => s.value === id)?.label)
          .join(",")
      );
      formDataToSend.append("job_posted_to", electricianListSelect.map((e) => e.value).join(","));
      formDataToSend.append("workforce_ids", workforceListSelect.map((w) => w.value).join(","));
      formDataToSend.append("assigned_by", user.id);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("map_location", mapLocation);
      formDataToSend.append("enableGeofencing", enableGeofencing ? 1 : 0);
      formDataToSend.append("radius", radius);
      formDataToSend.append("showCustomerInfo", showCustomerInfo ? 1 : 0);

      if (enableAttachments && attachments.length > 0) {
        attachments.forEach((attachment, index) => {
          formDataToSend.append(`attachments`, attachment.file);
        });
      }

      const jobResponse = await fetch(`${API_URL}/api/job/create`, {
        method: "POST",
        body: formDataToSend,
      });
      const jobData = await jobResponse.json();

      if (jobResponse.status !== 201) {
        throw new Error(jobData.message || "Failed to create job");
      }

      if (generateInvoice) {
        const invoiceBody = JSON.stringify({
          invoice_number: invoiceData.invoiceNumber,
          customer_name: customerData.name,
          phone: customerData.phone,
          address: customerData.address,
          email: customerData.email,
          issue_date: invoiceData.issuedDate,
          order_number: invoiceData.orderNumber,
          due_date: invoiceData.dueDate,
          amount: calculateTotal(),
          discount,
          tax: taxAmount,
          job_id: jobData.job?.id || null,
          items: invoiceItems.map((item) => ({
            id: item.id,
            itemName: item.itemName,
            qty: item.qty,
            unitPrice: item.unitPrice,
            price: item.price,
            description: item.description,
          })),
          company_id: selectedCompany?.value || null,
          customer_id: customerData.id,
          type_of_invoice: "invoice",
        });

        const invoiceResponse = await fetch(`${API_URL}/api/invoice/create`, {
          method: "POST",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
          body: invoiceBody,
        });
        const invoiceDataResponse = await invoiceResponse.json();

        if (invoiceResponse.status !== 201) {
          throw new Error(
            invoiceDataResponse.message || "Failed to create invoice"
          );
        }

        setAlert({
          show: true,
          type: "primary",
          message: "Job and order form created successfully!",
        });
        Swal.fire({
          title: "Success",
          text: "Your job and order form have been created successfully.",
          icon: "success",
        });
      } else {
        setAlert({
          show: true,
          type: "primary",
          message: "Job created successfully!",
        });
        Swal.fire({
          title: "Success",
          text: "Your job has been created successfully.",
          icon: "success",
        });
      }

      resetForm();
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message:
          error.message || "An error occurred while processing your request.",
      });
    }
  };

  const professionalTemplate = `

  <!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Form Rw4620</title>
      <style>
        /* Ensure consistent styling for Quill content */
        .ql-align-center { text-align: center; }
        .ql-align-right { text-align: right; }
        td ul, td ol { margin-left: 20px; padding-left: 0; }
        td li { margin-bottom: 5px; }
                         
                        
      </style>
  </head>
  <body style="font-family: Arial, sans-serif; margin: 0; padding: 40px; font-size: 12px; color: #000;">
      <!-- Page 1 -->
      <div style="width: 100%; height: auto; margin: 0 auto; padding-top: 20px; box-sizing: border-box;">
          <!-- Header -->
          <table style="width: 100%; border-collapse: collapse;">
              <tr>
                  <td style="width: 50%; vertical-align: top;padding-left:15px;">
                      <div style="font-weight: bold; font-size: 14px;">
                      <img src="{{business_logo}}" style="width:140px;padding-left:10px"/>
                      </div>
                      <div style="font-weight: bold; font-size: 14px;padding-left:30px">{{business_header}}</div>
                      <div style="width:150px;text-align:center;font-size:13px">{{business_address}}</div>
                      <div style="font-size:13px;padding-left:10px">{{business_email}}</div>
                      <div style="font-size:13px;padding-left:10px">VAT NO: {{vat_reg_no}}</div>
                  </td>
                  <td style="width: 50%; text-align: right; vertical-align: top;padding-right:20px">
                      <div style="font-size:13px;">Issue Date: {{issue_date}}</div>
                  </td>
              </tr>
          </table>
          <!-- Bill To -->
          <div style="text-align:center;padding-top:10px">
          <p style="font-weight: 600; font-size: 18px;">{{type}}</p>
          </div>
          <div style="border-top:3px solid #d17f15;margin-top:10px"/>
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
              <tr>
                  <td style="width: 70%; vertical-align: top;padding-left:10px">
                      <div style="font-weight: bold;">For:</div>
                      <div>{{customer_name}}</div>
                      <div>{{customer_phone}}</div>
                      <div>{{customer_address}}</div>
                  </td>
                  <td style="width: 30%;text-align:left">
                  <div>Order Form No: {{invoice_number}}</div>
                  {{order_number}}
                  </td>
              </tr>
          </table>
          <!-- Description Table -->
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
              <tr style="background-color: #d17f15;">
                  <th style="text-align: left; padding: 5px; border-top: 2px solid #d17f15;border-bottom: 2px solid #d17f15;color:white">Description</th>
                  <th style="text-align: center; padding: 5px; border-top: 2px solid #d17f15;border-bottom: 2px solid #d17f15;color:white">Quantity</th>
                  <th style="text-align: center; padding: 5px; border-top: 2px solid #d17f15;border-bottom: 2px solid #d17f15;color:white">Unit Price</th>
                  <th style="text-align: right; padding: 5px; border-top: 2px solid #d17f15;border-bottom: 2px solid #d17f15;color:white">Cost</th>
              </tr>
              {{service_items}}
          </table>
          <!-- Payment Instructions -->
          <table style="width: 100%; border-collapse: collapse; margin-top: 20px;padding:10px">
              <tr>
                  <td style="width: 50%; vertical-align: top; background: #f2f2f2; border:3px solid #d17f15; text-align:center; padding: 10px;">
                      <div style="font-weight: bold;">Payment Instructions</div>
                      <div>{{accountTitle}}</div>
                      <div>Sort code: {{sortCode}}</div>
                      <div>Account no: {{accountNumber}}</div>
                  </td>
                  <td style="width: 50%; vertical-align: top; text-align: right;">
                      <div style="padding:5px;background:#f3f3f3">Total Net Amount: £{{subtotal}}</div>
                      {{discount}}
                      {{tax}}
                      <div style="border:1px solid #d17f15;"></div>
                      <div style="font-weight: bold;padding:5px">Order Form Total : £{{total}}</div>
                      <div style="border:1px solid #d17f15;"></div>
                  </td>
              </tr>
          </table>
          <!-- Terms -->
         
      </div>
      <!-- Page 2 -->
      <div style="width: 100%; height: 842px; margin: 0px auto; padding: 10px; box-sizing: border-box; page-break-before: always;">
          <!-- Terms and Conditions -->
          <div style="margin-top: 20px;text-align:center">
              <div style="font-weight: bold;font-size:14px;">Terms and Conditions:</div>
              <div style="margin-top: 10px;font-size:12px">
                  <div><strong>Payment Due Date:</strong> Full payment is due within 14 days from the order form date.</div>
                  <div><strong>Late Payments:</strong> A late fee of 5% of the total order form amount will be applied to any payment not received by the due date. Interest on overdue balances will accrue at 5% per week until payment is received in full.</div>
                  <div><strong>Methods of Payment:</strong> Payment can be made via bank transfer. Please ensure the order form number is included in all payments for proper processing.</div>
                  <div><strong>Disputes:</strong> Any disputes regarding the order form must be reported in writing within 7 days of the order form date. Disputing any portion of the order form does not suspend the obligation to pay the undisputed amount by the due date.</div>
                  <div><strong>Suspension of Service:</strong> Non-payment may result in suspension of service or further legal action, at the discretion of R-Wire Limited.</div>
              </div>
              <div style="margin-top: 20px;">
                  <div style="font-weight: bold;style="font-size:14px"">Disclaimer:</div>
                  <div style="font-size:12px">If payment of order forms is not received, we reserve the right to recover any cost, commissions or fees incurred for the collection of payments including those which may be incurred by using a debt collection agency. There will be a 5% late payment fee applied every 7 days after the due date of the order form.</div>
              </div>
          </div>
          <!-- Signature -->
          <div style="margin-top: 40px;text-align:center">
              <div style="font-size:12px"><strong>{{business_footer}}</strong></div>
              <div style="font-size:12px">{{issue_date_footer}}</div>
              <div style="font-size:12px">{{business_phone}}</div>
          </div>
          <div style="display: flex; column-gap: 10px; justify-content: center; border-top: 1px solid #c0c0c0; padding-top: 22px; margin-top: 20px;">
              <div style="width:15%">
                  <img style="width: 127px;" src="https://api.test.frontforcecrm.com/logo/mainlogo.png">
              </div>
              <div style="width:10%">
                  <img style="width: 110px;" src="https://api.test.frontforcecrm.com/logo/child.webp">
              </div>
              <div style="width:15%">
                  <img style="width: 100px;" src="https://api.test.frontforcecrm.com/logo/mini.png">
              </div>
              <div style="width: 20%;">
                  <img style="width: 140px;" src="https://api.test.frontforcecrm.com/logo/trustmark.jpeg">
              </div>
              <div style="width:20%">
                  <img style="width: 195px;" src="https://api.test.frontforcecrm.com/logo/last.jpeg">
              </div>
          </div>
      </div>
  </body>
  </html>
  `;


  const renderTemplate = () => {
    function checkOddEven(index) {
      return index % 2 === 0 ? "Even" : "Odd";
    }

    function convertDateFormat(dateString) {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }

    const serviceItems = invoiceItems
      .map((item, index) => {
        const descriptionHtml = item.description || "N/A";
        const rowStyle = checkOddEven(index) === "Even" ? "" : "style='background:#f3f3f3'";
        return `
          <tr ${rowStyle}>
            <td style="padding: 5px; vertical-align: top; border: 1px dotted #c0c0c0; border-left: 1px dotted #c0c0c0; font-size:14px">
              ${item.itemName?.label || item.itemName?.value || "N/A"}<br>
              <div>${descriptionHtml}</div>
            </td>
            <td style="text-align: center; padding: 5px; border: 1px dotted #c0c0c0; font-size:14px">${item.qty}</td>
            <td style="text-align: center; padding: 5px; border: 1px dotted #c0c0c0; font-size:14px">£${item.unitPrice.toFixed(2)}</td>
            <td style="text-align: right; padding: 5px; border: 1px dotted #c0c0c0; font-size:14px">£${item.price}</td>
          </tr>
        `;
      })
      .join("");

    return professionalTemplate
      .replace("{{business_logo}}", API_URL + companyData.logo || "./assets/images/logo.png")
      .replace("{{business_header}}", companyData.name || "Your Company")
      .replace("{{business_address}}", companyData.address || "Your Address")
      .replace("{{business_phone}}", companyData.phone || "Your Phone")
      .replace("{{business_email}}", companyData.email || "Your Email")
      .replace("{{vat_reg_no}}", companyData.vatRegNo || "Your VAT Reg No")
      .replace("{{invoice_number}}", invoiceData.invoiceNumber || "N/A")
      .replace(
        "{{order_number}}",
        invoiceData.orderNumber ? `<div>Order No: ${invoiceData.orderNumber}</div>` : ""
      )
      .replace("{{issue_date}}", convertDateFormat(invoiceData.issuedDate) || "N/A")
      .replace("{{issue_date_footer}}", convertDateFormat(invoiceData.issuedDate) || "N/A")
      .replace("{{type}}", "Order Form")
      .replace("{{customer_name}}", formData.client_name || "N/A")
      .replace("{{customer_address}}", formData.address || "N/A")
      .replace("{{customer_phone}}", formData.phone || "N/A")
      .replace("{{accountNumber}}", companyData.accountNumber || "N/A")
      .replace("{{sortCode}}", companyData.sortCode || "N/A")
      .replace("{{accountTitle}}", companyData.accountTitle || "N/A")
      .replace("{{service_items}}", serviceItems)
      .replace("{{subtotal}}", calculateSubtotal().toFixed(2))
      .replace(
        "{{discount}}",
        discount === 0
          ? "<div style='padding:15px;'></div>"
          : `<div style="padding:5px">Discount: £${calculateDiscount().toFixed(2)}</div>`
      )
      .replace(
        "{{tax}}",
        taxAmount !== 0
          ? `<div style="padding:5px;">Total VAT Amount: £${calculateTax().toFixed(2)}</div>`
          : "<div style='padding:15px;'></div>"
      )
      .replace("{{total}}", calculateTotal().toFixed(2))
      .replace("{{business_footer}}", companyData.name || "Your Company");
  };

  const steps = [
    { label: "Customer Information", icon: "bi:person-fill" },
    { label: "Job Information", icon: "bi:tools" },
    ...(generateInvoice
      ? [
          { label: "Order Form Details", icon: "bi:file-earmark-text-fill" },
          { label: "Add Items", icon: "bi:cart-fill" },
          { label: "Preview", icon: "bi:eye-fill" },
        ]
      : []),
  ];

  return (
    <div className="container py-5" style={{ direction: "ltr" }}>
      <style>
        {`
          .ql-container {
            font-family: Arial, sans-serif;
            font-size: 14px;
            min-height: 150px;
            border: 1px solid #ced4da;
            border-radius: 8px;
            direction: ltr !important;
            text-align: left !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }
          .ql-container.is-invalid {
            border-color: #dc3545;
          }
          .ql-editor {
            min-height: 120px;
            padding: 12px;
            direction: ltr !important;
            text-align: left !important;
            unicode-bidi: embed;
          }
          .ql-editor:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          .ql-toolbar {
            border-bottom: 1px solid #ced4da;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            background-color: #f8f9fa;
            direction: ltr !important;
          }
          .modal-fullscreen {
            max-width: 95%;
            margin: auto;
          }
          .modal-content {
            max-height: 90vh;
            overflow-y: auto;
            border-radius: 12px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          }
          .step-container {
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          }
          .step-item {
            flex: 1;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;
            z-index: 1;
          }
          .step-item.disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }
          .step-icon {
            width: 50px;
            height: 50px;
            line-height: 50px;
            border-radius: 50%;
            font-size: 24px;
            margin: 0 auto 0.5rem;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid transparent;
          }
          .step-item.active .step-icon {
            background-color: #007bff;
            color: white;
            border-color: #0056b3;
            box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
          }
          .step-item.completed .step-icon {
            background-color: #28a745;
            color: white;
            border-color: #1e7e34;
          }
          .step-item:not(.active):not(.completed) .step-icon {
            background-color: #e9ecef;
            color: #6c757d;
          }
          .step-item:hover:not(.disabled) .step-icon {
            transform: scale(1.1);
          }
          .step-label {
            font-size: 0.9rem;
            font-weight: 500;
            color: #343a40;
          }
          .step-item.active .step-label {
            color: #007bff;
            font-weight: 600;
          }
          .step-item.completed .step-label {
            color: #28a745;
          }
          .progress-connector {
            position: absolute;
            top: 25px;
            left: 12.5%;
            right: 12.5%;
            height: 4px;
            background-color: #e9ecef;
            z-index: 0;
          }
          .progress-bar {
            transition: width 0.3s ease;
          }
          .card {
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          .card-header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
            padding: 1.5rem;
          }
          .form-label {
            font-weight: 600;
            color: #343a40;
          }
          .form-control, .form-select {
            border-radius: 8px;
            padding: 0.75rem;
            transition: all 0.2s ease;
          }
          .form-control:focus, .form-select:focus {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
          .btn {
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }
          .btn-primary {
            background-color: #007bff;
            border-color: #007bff;
          }
          .btn-primary:hover {
            background-color: #0056b3;
            border-color: #004085;
          }
          .btn-success {
            background-color: #28a745;
            border-color: #28a745;
          }
          .btn-success:hover {
            background-color: #1e7e34;
            border-color: #1c7430;
          }
          .btn-secondary {
            background-color: #6c757d;
            border-color: #6c757d;
          }
          .btn-secondary:hover {
            background-color: #5a6268;
            border-color: #545b62;
          }
          .alert {
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1.5rem;
          }
          .table {
            border-radius: 8px;
            overflow: hidden;
          }
          .table thead {
            background-color: #f8f9fa;
          }
          .table tbody tr:hover {
            background-color: #f1f3f5;
          }
          .address-suggestions {
            position: absolute;
            z-index: 1000;
            background: white;
            border: 1px solid #ced4da;
            border-radius: 8px;
            width: 100%;
            max-height: 200px;
            overflow-y: auto;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            margin-top: 0.25rem;
          }
          .address-suggestions li {
            padding: 0.75rem;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }
          .address-suggestions li:hover {
            background-color: #f8f9fa;
          }
          dropzone {
            border: 2px dashed #ced4da;
            border-radius: 8px;
            padding: 20px;
            text-align: center;
            background-color: #f8f9fa;
            transition: all 0.3s ease;
          }
          .dropzone.dragover {
            border-color: #007bff;
            background-color: #e6f0ff;
          }
          .attachment-preview {
            position: relative;
            display: inline-block;
            margin: 10px;
          }
          .attachment-preview img, .attachment-preview video {
            max-width: 150px;
            max-height: 150px;
            border-radius: 8px;
            object-fit: cover;
          }
          .attachment-preview .remove-btn {
            position: absolute;
            top: -10px;
            right: -10px;
            background-color: #dc3545;
            color: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
        `}
      </style>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow">
              <div className="card-header">
                <h3 className="h6 mb-0 text-center">Create New Job</h3>
              </div>
              <div className="card-body p-4">
                <div className="step-container">
                  <div className="progress-connector">
                    <div
                      className="progress-bar bg-primary"
                      style={{
                        width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                      }}
                    ></div>
                  </div>
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step-item ${
                        currentStep === index + 1 ? "active" : ""
                      } ${currentStep > index + 1 ? "completed" : ""} ${
                        currentStep < index + 1 ? "disabled" : ""
                      }`}
                      onClick={() => handleStepClick(index + 1)}
                    >
                      <div className="step-icon">
                        <Icon icon={step.icon} />
                      </div>
                      <div className="step-label">{step.label}</div>
                    </div>
                  ))}
                </div>

                {alert.show && (
                  <div
                    className={`alert alert-${alert.type} alert-dismissible fade show`}
                    role="alert"
                  >
                    {alert.message}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setAlert({ show: false, type: "", message: "" })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                <form onSubmit={onFormSubmission} noValidate>
                  {currentStep === 1 && (
                    <div className="p-20 rounded">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                        <label htmlFor="referenceNo" className="form-label">
                          Reference No <span className="text-danger">*</span>
                        </label>
                        <input
                              type="text"
                              id="referenceNo"
                              name="referenceNo"
                              value={referenceNo}
                              className={`form-control ${
                                errors.client_name ? "is-invalid" : ""
                              }`}
                              onChange={(e)=>setreferenceNo(e.target.value)}
                            />
                              {errors.referenceNo && (
                            <div className="invalid-feedback">
                              {errors.referenceNo}
                            </div>
                          )}
                        </div>

                      </div>
                      <h4 className="h6 mb-4">Customer Information</h4>
                      <div className="mb-4">
                        <label className="form-label">
                          Customer Type <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex gap-3">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              id="newCustomer"
                              name="customerType"
                              value="new"
                              checked={customerType === "new"}
                              onChange={handleCustomerTypeChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="newCustomer"
                            >
                              New Customer
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              id="existingCustomer"
                              name="customerType"
                              value="existing"
                              checked={customerType === "existing"}
                              onChange={handleCustomerTypeChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="existingCustomer"
                            >
                              Existing Customer
                            </label>
                          </div>
                        </div>
                      </div>

                      {customerType === "existing" && (
                        <div className="mb-4">
                          <label
                            htmlFor="customerSelect"
                            className="form-label"
                          >
                            Select Customer <span className="text-danger">*</span>
                          </label>
                          <Select
                            id="customerSelect"
                            options={customerList}
                            onChange={handleCustomerChange}
                            value={selectedCustomer}
                            isClearable
                            placeholder="Select a customer"
                            className={errors.customer ? "is-invalid" : ""}
                          />
                          {errors.customer && (
                            <div className="invalid-feedback">
                              {errors.customer}
                            </div>
                          )}
                        </div>
                      )}

                      {(customerType === "new" || selectedCustomer) && (
                        <div className="row g-3">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="client_name" className="form-label">
                              Client Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.client_name ? "is-invalid" : ""
                              }`}
                              id="client_name"
                              name="client_name"
                              placeholder="Enter Full Name"
                              value={formData.client_name}
                              onChange={handleInputChange}
                              readOnly={customerType === "existing"}
                            />
                            {errors.client_name && (
                              <div className="invalid-feedback">
                                {errors.client_name}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="email" className="form-label">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className={`form-control ${
                                errors.email ? "is-invalid" : ""
                              }`}
                              id="email"
                              name="email"
                              placeholder="Enter email address"
                              value={formData.email}
                              onChange={handleInputChange}
                              readOnly={customerType === "existing"}
                            />
                            {errors.email && (
                              <div className="invalid-feedback">
                                {errors.email}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="phone" className="form-label">
                              Phone
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              id="phone"
                              name="phone"
                              placeholder="Enter phone number"
                              value={formData.phone}
                              onChange={handleInputChange}
                              readOnly={customerType === "existing"}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="address" className="form-label">
                              Address{" "}
                              <span
                                className={
                                  customerType === "new" ? "text-danger" : ""
                                }
                              >
                                *
                              </span>
                            </label>
                            {customerType === "new" ? (
                              <div style={{ position: "relative" }}>
                                <input
                                  type="text"
                                  className={`form-control ${
                                    errors.address ? "is-invalid" : ""
                                  }`}
                                  id="address"
                                  name="address"
                                  placeholder="Enter address"
                                  value={formData.address}
                                  onChange={handleInputChange}
                                  ref={addressInputRef}
                                />
                                {errors.address && (
                                  <div className="invalid-feedback">
                                    {errors.address}
                                  </div>
                                )}
                                {addressSuggestions.length > 0 && (
                                  <ul className="address-suggestions">
                                    {addressSuggestions.map(
                                      (suggestion, index) => (
                                        <li
                                          key={index}
                                          onClick={() =>
                                            handleAddressSelect(suggestion)
                                          }
                                        >
                                          {suggestion.label}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}
                              </div>
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                id="address"
                                name="address"
                                placeholder="Enter address"
                                value={formData.address}
                                readOnly
                              />
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="postcode" className="form-label">
                              Postcode
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="postcode"
                              name="postcode"
                              placeholder="Enter postcode"
                              value={formData.postcode}
                              onChange={handleInputChange}
                              readOnly={customerType === "existing"}
                            />
                          </div>
                        </div>
                      )}
                       <div className="mb-3  mt-3">
                        <div className="form-check form-switch">
                          <Switch
                            className="form-check-input"
                            type="checkbox"
                            id="jobLocationSwitch"
                            checked={showCustomerInfo}
                            onChange={(e) => {
                              setShowCustomerInfo(e);
                              
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="jobLocationSwitch"
                          >
                            &nbsp; &nbsp;Show Customer Information to electrician?
                          </label>
                        </div>
                      </div>
                      <div className="mb-3  mt-3">
                        <div className="form-check form-switch">
                          <Switch
                            className="form-check-input"
                            type="checkbox"
                            id="jobLocationSwitch"
                            checked={isJobLocationCustomerAddress}
                            onChange={(e) => {
                              setIsJobLocationCustomerAddress(e);
                              if (e) {
                                setJobLocation("");
                                setJobLocationCoordinates(null);
                                setJobLocationSuggestions([]);
                              }
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="jobLocationSwitch"
                          >
                            &nbsp; &nbsp;Job Location is Customer Address
                          </label>
                        </div>
                      </div>
                      <div className="row">
                      {!isJobLocationCustomerAddress && (
                       <>
                        <div className="col-md-8 mb-4">
                          <label htmlFor="jobLocation" className="form-label">
                            Job Location <span className="text-danger">*</span>
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.jobLocation ? "is-invalid" : ""
                              }`}
                              id="jobLocation"
                              name="jobLocation"
                              placeholder="Enter job location"
                              value={jobLocation}
                              onChange={(e) => setJobLocation(e.target.value)}
                              ref={jobLocationInputRef}
                            />
                            {errors.jobLocation && (
                              <div className="invalid-feedback">
                                {errors.jobLocation}
                              </div>
                            )}
                            {jobLocationSuggestions.length > 0 && (
                              <ul className="address-suggestions">
                                {jobLocationSuggestions.map(
                                  (suggestion, index) => (
                                    <li
                                      key={index}
                                      onClick={() =>
                                        handleJobLocationSelect(suggestion)
                                      }
                                    >
                                      {suggestion.label}
                                    </li>
                                  )
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4 mb-4">
                          <label htmlFor="jobPostcode" className="form-label">
                            Job PostCode <span className="text-danger">*</span>
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.jobLocation ? "is-invalid" : ""
                              }`}
                              id="jobPostcode"
                              name="jobPostcode"
                              placeholder="Enter postcode"
                              value={jobPostcode}
                              onChange={(e) => setJobPostcode(e.target.value)}
                            />
                               {errors.jobPostcode && (
                              <div className="invalid-feedback">
                                {errors.jobPostcode}
                              </div>
                            )}
                         
                          </div>
                        </div>

                       </>
                      )}
                      </div>
                      <div className="mb-4 mt-3">
                        <div className="form-check form-switch">
                          <Switch
                            className="form-check-input"
                            type="checkbox"
                            id="geofencingSwitch"
                            checked={enableGeofencing}
                            onChange={(e) =>
                              setEnableGeofencing(e)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="geofencingSwitch"
                          >
                            &nbsp; &nbsp;Enable Geofencing
                          </label>
                        </div>
                      </div>
                      {enableGeofencing && (
                        <>
                          <div className="mb-4">
                            <label htmlFor="radiusInput" className="form-label">
                              Geofence Radius (meters)
                            </label>
                            <input
                              type="number"
                              id="radiusInput"
                              className="form-control"
                              min="50"
                              max="5000"
                              value={radius}
                              onChange={(e) =>
                                setRadius(parseInt(e.target.value) || 100)
                              }
                            />
                            <div className="form-text">
                              Set the radius for the geofence area around the location.
                            </div>
                          </div>
                          {loadError && (
                            <div className="alert alert-danger">
                              Error loading Google Maps. Please try again later.
                            </div>
                          )}
                          {!isLoaded ? (
                            <div>Loading Google Maps...</div>
                          ) : (
                            <div className="mb-4">
                              <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={mapCenter}
                                zoom={14}
                                options={{
                                  streetViewControl: false,
                                  mapTypeControl: false,
                                  fullscreenControl: false,
                                }}
                              >
                                {markerPosition && (
                                  <>
                                    <Marker
                                      position={markerPosition}
                                      draggable={false}
                                    />
                                    <Circle
                                      center={markerPosition}
                                      radius={radius}
                                      options={{
                                        strokeColor: "#FF0000",
                                        strokeOpacity: 0.8,
                                        strokeWeight: 2,
                                        fillColor: "#FF0000",
                                        fillOpacity: 0.35,
                                      }}
                                    />
                                  </>
                                )}
                              </GoogleMap>
                              <div className="form-text">
                                Map showing the job location and geofence area.
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="p-20 rounded">
                      <h4 className="h6 mb-4">Job Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="electricianSelect"
                            className="form-label"
                          >
                            Select Electricians <span className="text-danger">*</span>
                          </label>
                          <Select
                            id="electricianSelect"
                            options={electricianList}
                            onChange={handleChangeElectrician}
                            isMulti
                            value={electricianListSelect}
                            className={errors.electricians ? "is-invalid" : ""}
                          />
                          {errors.electricians && (
                            <div className="invalid-feedback">
                              {errors.electricians}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="workforceSelect"
                            className="form-label"
                          >
                            Select Workforce <span className="text-danger">*</span>
                          </label>
                          <Select
                            id="workforceSelect"
                            options={workforceList}
                            onChange={handleChangeWorkforce}
                            isMulti
                            value={workforceListSelect}
                            className={errors.workforce ? "is-invalid" : ""}
                            placeholder="Select workforce members"
                          />
                          {errors.workforce && (
                            <div className="invalid-feedback">
                              {errors.workforce}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="serviceSelect" className="form-label">
                            Select Services <span className="text-danger">*</span>
                          </label>
                          <Select
                            id="serviceSelect"
                            options={optionsForServices}
                            onChange={handleChangeService}
                            isMulti
                            value={optionsForServices.filter((option) =>
                              selectedServices.includes(option.value)
                            )}
                            className={errors.services ? "is-invalid" : ""}
                            placeholder="Select services"
                          />
                          {errors.services && (
                            <div className="invalid-feedback">
                              {errors.services}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="job_cost" className="form-label">
                            Job Cost
                          </label>
                          <input
                            type="number"
                            min={0}
                            className="form-control"
                            id="job_cost"
                            name="job_cost"
                            placeholder="0.00"
                            value={formData.job_cost}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-md-12 mb-3">
                          <label htmlFor="description" className="form-label">
                            Description
                          </label>
                          <div
                            ref={quillRefs.jobDescription}
                            className={`ql-container ${
                              errors.description ? "is-invalid" : ""
                            }`}
                          />
                          {errors.description && (
                            <div className="invalid-feedback">
                              {errors.description}
                            </div>
                          )}
                        </div>
                        <div className="col-md-12 mb-3" style={{paddingTop: 90}}>
                          <div className="form-check form-switch">
                            <Switch
                              className="form-check-input"
                              type="checkbox"
                              id="attachmentsSwitch"
                              checked={enableAttachments}
                              onChange={(e) => {
                                setEnableAttachments(e);
                                if (!e) {
                                  attachments.forEach((attachment) =>
                                    URL.revokeObjectURL(attachment.url)
                                  );
                                  setAttachments([]);
                                  setAttachmentError("");
                                }
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="attachmentsSwitch"
                            >
                              &nbsp; &nbsp;Add Attachments
                            </label>
                          </div>
                        </div>
                        {enableAttachments && (
                          <div className="col-md-12 mb-3">
                            <div
                              className="dropzone"
                              onDrop={handleDrop}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.add("dragover");
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.classList.remove("dragover");
                              }}
                            >
                              <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept="image/jpeg,image/png,video/mp4,video/mpeg"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                              />
                              <p>
                                Drag and drop images or videos here, or{" "}
                                <button
                                  type="button"
                                  className="btn btn-link p-0"
                                  onClick={() => fileInputRef.current.click()}
                                >
                                  click to select files
                                </button>
                              </p>
                              <div className="form-text">
                                Maximum 5 files (images or videos: JPEG, PNG, MP4, MPEG)
                              </div>
                              {attachmentError && (
                                <div className="text-danger mt-2">
                                  {attachmentError}
                                </div>
                              )}
                            </div>
                            {attachments.length > 0 && (
                              <div className="mt-3">
                                <h6>Uploaded Files:</h6>
                                <div className="d-flex flex-wrap">
                                  {attachments.map((attachment, index) => (
                                    <div key={index} className="attachment-preview">
                                      {attachment.type === "image" ? (
                                        <img
                                          src={attachment.url}
                                          alt={attachment.file.name}
                                        />
                                      ) : (
                                        <video
                                          src={attachment.url}
                                          controls
                                          style={{ maxWidth: "150px" }}
                                        />
                                      )}
                                      <button
                                        className="remove-btn"
                                        onClick={() => removeAttachment(index)}
                                      >
                                        <Icon icon="ic:twotone-close" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="col-md-12 mb-3 mt-3" style={{paddingTop: 90}}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="generateInvoice"
                              checked={generateInvoice}
                              onChange={(e) =>
                                setGenerateInvoice(e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="generateInvoice"
                            >
                              Generate Order Form
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {generateInvoice && currentStep === 3 && (
                    <div className="p-20 rounded">
                      <h4 className="h6 mb-4">Order Form Details</h4>
                      <div className="row g-3">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="invoiceNumber" className="form-label">
                            Order Form Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="invoiceNumber"
                            disabled
                            value={invoiceData.invoiceNumber}
                            onChange={(e) =>
                              handleInvoiceChange("invoiceNumber", e.target.value)
                            }
                            className={`form-control ${
                              errors.invoiceNumber ? "is-invalid" : ""
                            }`}
                          />
                          <div className="form-text">e.g., INV-001</div>
                          {errors.invoiceNumber && (
                            <div className="invalid-feedback">
                              {errors.invoiceNumber}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="issuedDate" className="form-label">
                            Issued Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            id="issuedDate"
                            value={invoiceData.issuedDate}
                            onChange={(e) =>
                              handleInvoiceChange("issuedDate", e.target.value)
                            }
                            className={`form-control ${
                              errors.issuedDate ? "is-invalid" : ""
                            }`}
                          />
                          <div className="form-text">Select the issue date.</div>
                          {errors.issuedDate && (
                            <div className="invalid-feedback">
                              {errors.issuedDate}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="dueDate" className="form-label">
                            Due Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            id="dueDate"
                            value={invoiceData.dueDate}
                            onChange={(e) =>
                              handleInvoiceChange("dueDate", e.target.value)
                            }
                            className={`form-control ${
                              errors.dueDate ? "is-invalid" : ""
                            }`}
                          />
                          <div className="form-text">Select the due date.</div>
                          {errors.dueDate && (
                            <div className="invalid-feedback">
                              {errors.dueDate}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="orderNumber" className="form-label">
                            Order Number
                          </label>
                          <input
                            type="text"
                            id="orderNumber"
                            value={invoiceData.orderNumber}
                            onChange={(e) =>
                              handleInvoiceChange("orderNumber", e.target.value)
                            }
                            className="form-control"
                            placeholder="Enter order number"
                          />
                          <div className="form-text">e.g., 12233 (optional)</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {generateInvoice && currentStep === 4 && (
                    <div className="p-20 rounded">
                      <h4 className="h6 mb-4">Add Items</h4>
                      <div className="mb-4">
                        <div className="card-body p-4">
                          <div className="row g-3 align-items-end">
                            <div className="col-md-6 mb-3">
                              <label htmlFor="itemType" className="form-label">
                                Item Type
                              </label>
                              <select
                                id="itemType"
                                value={newItem.itemType}
                                onChange={(e) => {
                                  setNewItem({
                                    itemType: e.target.value,
                                    itemName: null,
                                    qty: "",
                                    unitPrice: "",
                                    description: "",
                                  });
                                  if (quillInstances.invoiceItem.current) {
                                    quillInstances.invoiceItem.current.root.innerHTML = "";
                                  }
                                  setErrors({});
                                }}
                                className="form-select"
                              >
                                <option value="stock">Stock</option>
                                <option value="service">Service</option>
                              </select>
                              <div className="form-text">Select item type.</div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label htmlFor="itemName" className="form-label">
                                {newItem.itemType === "stock" ? "Stock Item" : "Service"}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Select
                                inputId="itemName"
                                options={
                                  newItem.itemType === "stock"
                                    ? stockList
                                    : optionsForServices
                                }
                                value={newItem.itemName}
                                onChange={(e) =>
                                  newItem.itemType === "stock"
                                    ? onChangeStock(e)
                                    : onChangeService(e)
                                }
                                className={`react-select ${
                                  errors.itemName ? "is-invalid" : ""
                                }`}
                                classNamePrefix="select"
                                placeholder={`Select ${
                                  newItem.itemType === "stock" ? "stock item" : "service"
                                }`}
                              />
                              <div className="form-text">
                                Select {newItem.itemType === "stock" ? "an item from stock" : "a service"}.
                              </div>
                              {errors.itemName && (
                                <div className="invalid-feedback d-block">
                                  {errors.itemName}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <label htmlFor="unitPrice" className="form-label">
                                Unit Price <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                id="unitPrice"
                                value={newItem.unitPrice}
                                onChange={(e) =>
                                  handleItemChange("unitPrice", e.target.value)
                                }
                                className={`form-control ${
                                  errors.unitPrice ? "is-invalid" : ""
                                }`}
                                placeholder="Unit Price"
                                readOnly={
                                  newItem.itemType === "stock" && newItem.itemName
                                }
                              />
                              <div className="form-text">Enter the unit price.</div>
                              {errors.unitPrice && (
                                <div className="invalid-feedback">
                                  {errors.unitPrice}
                                </div>
                              )}
                            </div>
                            {newItem.itemType === "stock" && (
                              <div className="col-md-6 mb-3">
                                <label htmlFor="qty" className="form-label">
                                  Quantity <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  id="qty"
                                  value={newItem.qty}
                                  onChange={(e) =>
                                    handleItemChange("qty", e.target.value)
                                  }
                                  className={`form-control ${
                                    errors.qty ? "is-invalid" : ""
                                  }`}
                                  placeholder="Quantity"
                                  min="1"
                                />
                                <div className="form-text">Enter the quantity.</div>
                                {errors.qty && (
                                  <div className="invalid-feedback">
                                    {errors.qty}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="col-md-12 mb-3">
                              <label
                                htmlFor="description"
                                className="form-label"
                              >
                                Description <span className="text-danger">*</span>
                              </label>
                              <div
                                ref={quillRefs.invoiceItem}
                                className={`ql-container ${
                                  errors.description ? "is-invalid" : ""
                                }`}
                              />
                              <div className="form-text">
                                Provide a detailed description of the item or service.
                              </div>
                              {errors.description && (
                                <div
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "0.875em",
                                  }}
                                >
                                  {errors.description}
                                </div>
                              )}
                            </div>
                            <div className="col-md-4">
                              <button
                                type="button"
                                onClick={addItem}
                                className="btn btn-success w-100"
                              >
                                <Icon
                                  icon="simple-line-icons:plus"
                                  className="me-2"
                                />
                                Add Item
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {errors.items && (
                        <div className="alert alert-danger mb-4">
                          {errors.items}
                        </div>
                      )}
                      {invoiceItems.length > 0 && (
                        <div className="table-responsive mb-4">
                          <table className="table table-bordered table-hover">
                            <thead>
                              <tr>
                                <th scope="col">SL.</th>
                                <th scope="col">Item</th>
                                <th scope="col">Quantity</th>
                                <th scope="col">Unit Price</th>
                                <th scope="col">Price</th>
                                <th scope="col" className="text-center">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceItems.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <p className="mb-1">
                                      {item.itemName?.label || item.itemName?.value}
                                    </p>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: item.description || "N/A",
                                      }}
                                    />
                                  </td>
                                  <td>{item.qty}</td>
                                  <td>£{item.unitPrice.toFixed(2)}</td>
                                  <td>£{item.price}</td>
                                  <td className="text-center">
                                    <button
                                      onClick={() => removeItem(item.id)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      <Icon icon="ic:twotone-close" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="row g-3">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="job_cost" className="form-label">
                            Job Cost
                          </label>
                          <input
                            type="number"
                            min={0}
                            className="form-control"
                            id="job_cost"
                            name="job_cost"
                            placeholder="0.00"
                            value={calculateTotal()}
                            disabled
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="discount" className="form-label">
                            Discount (%)
                          </label>
                          <input
                            type="number"
                            id="discount"
                            value={discount}
                            onChange={(e) => setDiscount(Number(e.target.value))}
                            className="form-control"
                            placeholder="Enter discount"
                            min="0"
                            max="100"
                          />
                          <div className="form-text">Enter discount percentage.</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="tax" className="form-label">
                            VAT (%)
                          </label>
                          <input
                            type="number"
                            id="tax"
                            value={taxAmount}
                            onChange={(e) => setTaxAmount(Number(e.target.value))}
                            className="form-control"
                            placeholder="Enter VAT"
                            min="0"
                            max="100"
                          />
                          <div className="form-text">Enter VAT percentage.</div>
                        </div>
                        <div className="col-12 text-end">
                          <p className="mb-1">
                            <strong>Total Net Amount:</strong> £{calculateSubtotal().toFixed(2)}
                          </p>
                          <p className="mb-1">
                            <strong>Discount:</strong> £{calculateDiscount().toFixed(2)}
                          </p>
                          <p className="mb-1">
                            <strong>VAT Amount:</strong> £{calculateTax().toFixed(2)}
                          </p>
                          <p className="mb-0">
                            <strong>Order Form Total:</strong> £{calculateTotal().toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {generateInvoice && currentStep === 5 && (
                    <div className="p-20 rounded">
                      <h4 className="h5 mb-4">Preview</h4>
                      <div className="mb-4">
                        <label htmlFor="companySelect" className="form-label">
                          Select Company <span className="text-danger">*</span>
                        </label>
                        <Select
                          id="companySelect"
                          options={companyList}
                          value={selectedCompany}
                          onChange={handleCompanyChange}
                          placeholder="Select a company"
                          className={errors.company ? "is-invalid" : ""}
                        />
                        {errors.company && (
                          <div className="invalid-feedback">
                            {errors.company}
                          </div>
                        )}
                      </div>
                      <div
                        className="border rounded p-4 bg-white"
                        style={{ maxHeight: "600px", overflowY: "auto" }}
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: renderTemplate() }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mt-5 p-20">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="btn btn-secondary"
                      >
                        <Icon icon="bi:arrow-left" className="me-2" />
                        Back
                      </button>
                    )}
                    <div className="d-flex gap-2">
                      {currentStep < (generateInvoice ? 5 : 2) && (
                        <button
                          type="button"
                          onClick={handleNext}
                          className="btn btn-primary"
                        >
                          Next
                          <Icon icon="bi:arrow-right" className="ms-2" />
                        </button>
                      )}
                      {(currentStep === 2 ||
                        (generateInvoice && currentStep === 5)) && (
                        <>
                          {generateInvoice && currentStep === 5 && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => setShowPreviewModal(true)}
                            >
                              <Icon icon="bi:eye" className="me-2" />
                              Preview Order Form
                            </button>
                          )}
                          <button type="submit" className="btn btn-success">
                            <Icon
                              icon="simple-line-icons:check"
                              className="me-2"
                            />
                            Submit
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </form>

                <div
                  className={`modal fade ${showPreviewModal ? "show d-block" : ""}`}
                  tabIndex="-1"
                  style={{
                    backgroundColor: showPreviewModal
                      ? "rgba(0,0,0,0.5)"
                      : "transparent",
                  }}
                >
                  <div className="modal-dialog modal-fullscreen">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Order Form Preview</h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowPreviewModal(false)}
                          aria-label="Close"
                        ></button>
                      </div>
                      <div className="modal-body" style={{height: 700}} >
                        <div
                        
                          dangerouslySetInnerHTML={{ __html: renderTemplate() }}
                        />
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowPreviewModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAppointmentLayer;